/**
 * Item conditions on the client (D98). The registry comes from the server
 * (GET /api/meta/product-types `conditions`, lib/catalog/conditions.js) through
 * useProductTypes(); these helpers are pure and mirror the server's check
 * (conditions.conditionProblems), which stays the authority. Without the registry
 * nothing is a known condition (fail closed): forms wait for it to load.
 */

/** Grades, best first (the server's conditions.GRADES). */
export const GRADES = ['A', 'B', 'C', 'D'];

/** Select options for the grades. */
export const gradeOptions = (grades = GRADES) => grades.map((g) => ({ value: g, label: `Grade ${g}` }));

/** What a condition change may carry; the server refuses anything else beside it (422). */
export const CONDITION_FIELDS = ['condition', 'grade', 'condition_reason'];

/** The other fields of a unit patch that changes the condition or grade ([] when it does not). */
export function mixedWithCondition(patch) {
  const keys = Object.keys(patch || {});
  if (!keys.includes('condition') && !keys.includes('grade')) return [];
  return keys.filter((k) => !CONDITION_FIELDS.includes(k));
}

export function findCondition(conditions = [], key) {
  return (conditions || []).find((c) => c.key === key) || null;
}

/**
 * What a condition requires for a product type (ADR 0010): the type's resolved rules from the registry
 * (`type.condition_rules`, type → industry → global), else the condition's global rules.
 */
export function requiresOf(c, type = null) {
  if (!c) return {};
  const own = type && type.condition_rules && type.condition_rules[c.key];
  return (own && own.requires) || c.requires || {};
}

/** Does this condition need a grade (for this type, when given)? */
export function gradeRequired(conditions, key, type = null) {
  const c = findCondition(conditions, key);
  return Boolean(c && requiresOf(c, type).grade);
}

/** Select options for the conditions a product type allows (all, when the type is unknown). */
export function conditionOptions(conditions = [], type = null) {
  const allowed = type && Array.isArray(type.conditions) ? type.conditions : null;
  return (conditions || []).filter((c) => !allowed || allowed.includes(c.key)).map((c) => ({ value: c.key, label: c.label }));
}

/** A CSV cell or form value as a condition key ("Open box" → "open_box"). */
export const conditionKey = (v) => String(v ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');

/**
 * { condition?, grade? } messages for one stock row: a known condition the type allows,
 * and a grade (one of `grades`) only where the condition needs one.
 */
export function conditionErrors({ condition, grade } = {}, { conditions = [], type = null, grades = [] } = {}) {
  const e = {};
  const key = conditionKey(condition);
  const c = findCondition(conditions, key);
  const g = String(grade ?? '').trim().toUpperCase();
  if (!key) e.condition = 'Pick a condition';
  else if (!c) e.condition = `condition must be one of ${(conditions || []).map((x) => x.key).join(', ')}`;
  else if (type && Array.isArray(type.conditions) && !type.conditions.includes(key)) e.condition = `${type.plural || type.label} cannot be sold as ${c.label.toLowerCase()}`;
  if (g && grades.length && !grades.includes(g)) e.grade = `grade must be one of ${grades.join(', ')}`;
  else if (!g && c && requiresOf(c, type).grade) e.grade = `A ${c.label.toLowerCase()} item needs a grade`;
  return e;
}
